import { ChevronRight, Dumbbell, MessageSquareText, Target } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

type Props = {
  weekOffset: number;
  remainingWorkouts: number;
  weeklyGoal: number;
  onBrowseTemplates: () => void;
  onOpenAiGenerator: () => void;
};

function getHeadline(weekOffset: number): string {
  if (weekOffset === 0) return "Ready to fill your week?";
  if (weekOffset < 0) return "Nothing on the calendar here";
  return "Your week is wide open";
}

function getBodyCopy(weekOffset: number, remainingWorkouts: number, weeklyGoal: number): string {
  if (weekOffset === 0 && remainingWorkouts > 0) {
    const workoutWord = remainingWorkouts === 1 ? "workout" : "workouts";
    return `You still have room for ${remainingWorkouts} ${workoutWord} toward your ${weeklyGoal}-workout weekly goal. Browse a template or let your AI trainer build one for you.`;
  }
  if (weekOffset === 0 && remainingWorkouts === 0) {
    return "You've hit your weekly goal — nice work! Want to add more? Browse a template or let your AI trainer build a custom session.";
  }
  if (weekOffset > 0) {
    return "Plan ahead by picking a workout template or asking your AI trainer to create something tailored for you.";
  }
  return "Every week is a fresh start. Browse a template or let your AI trainer build a session that fits your goals.";
}

export function ScheduleWeekEmptyState({
  weekOffset,
  remainingWorkouts,
  weeklyGoal,
  onBrowseTemplates,
  onOpenAiGenerator,
}: Props) {
  const headline = getHeadline(weekOffset);
  const body = getBodyCopy(weekOffset, remainingWorkouts, weeklyGoal);

  return (
    <Card className="border-primary/20 bg-dark-card shadow-card overflow-hidden">
      <CardContent className="p-6">
        <div className="flex flex-col items-center text-center mb-6">
          <div className="w-14 h-14 rounded-2xl bg-dark-slate border border-primary/15 flex items-center justify-center mb-4">
            <Dumbbell className="w-7 h-7 text-primary/70" />
          </div>
          <h4 className="text-white font-semibold text-lg mb-2">{headline}</h4>
          <p className="text-sm text-gray-light max-w-sm">{body}</p>
        </div>

        <div className="space-y-3">
          <Button
            variant="outline"
            className="w-full justify-between border-primary/30 bg-transparent text-white text-left p-4 h-auto hover:bg-dark-slate/80 hover:text-white hover:border-primary/40"
            onClick={onBrowseTemplates}
          >
            <div className="flex items-center">
              <div className="bg-primary/20 p-2 rounded-full mr-3">
                <Target className="h-5 w-5 text-primary" />
              </div>
              <div>
                <span className="font-medium block text-white">Browse workout templates</span>
                <span className="text-sm text-gray-light">Explore the workout library</span>
              </div>
            </div>
            <ChevronRight className="h-5 w-5 text-gray-400 shrink-0" />
          </Button>

          <Button
            variant="outline"
            className="w-full justify-between border-primary/30 bg-transparent text-white text-left p-4 h-auto hover:bg-dark-slate/80 hover:text-white hover:border-primary/40"
            onClick={onOpenAiGenerator}
          >
            <div className="flex items-center">
              <div className="bg-primary/20 p-2 rounded-full mr-3">
                <MessageSquareText className="h-5 w-5 text-primary" />
              </div>
              <div>
                <span className="font-medium block text-white">Ask AI Trainer</span>
                <span className="text-sm text-gray-light">Generate a custom workout</span>
              </div>
            </div>
            <ChevronRight className="h-5 w-5 text-gray-400 shrink-0" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
